import { all, put, takeEvery, fork, select } from "redux-saga/effects";
import { apiURL } from "../actions/helpers";
import axios from "axios";
import moment from "moment-timezone";

// Action Types
export const PAST_ALERTS_LOAD = "PAST_ALERTS/LOAD";
export const PAST_ALERTS_LOAD_SUCCESS = "PAST_ALERTS/LOAD/SUCCESS";
export const PAST_ALERTS_LOAD_FAILURE = "PAST_ALERTS/LOAD/FAILURE";
export const PAST_ALERTS_SET_PERIOD = "PAST_ALERTS/SET_PERIOD";
export const PAST_ALERTS_CACHE_HIT = "PAST_ALERTS/CACHE_HIT";

// Action Creators
export const pastAlertsLoad = () => ({
  type: PAST_ALERTS_LOAD,
});

export const pastAlertsLoadSuccess = (data, timespan) => ({
  type: PAST_ALERTS_LOAD_SUCCESS,
  data,
  timespan,
});

export const pastAlertsLoadFailure = (error) => ({
  type: PAST_ALERTS_LOAD_FAILURE,
  error,
});

export const pastAlertsSetPeriod = (period) => ({
  type: PAST_ALERTS_SET_PERIOD,
  period,
});

export const pastAlertsCacheHit = (timespan) => ({
  type: PAST_ALERTS_CACHE_HIT,
  timespan,
});

// Map UI period values to API timespan format
const mapPeriodToTimespan = (period) => {
  const mapping = {
    all: "all",
    "1M": "1m",
    "3M": "3m",
    "6M": "6m",
    YTD: "ytd",
    "1Y": "1y",
  };
  return mapping[period] || "6m";
};

// Format date string from "2025-10-10" to "FRIDAY, OCT 10, 2025"
// Uses UTC to match the payload dates exactly
const formatDateGroup = (dateString) => {
  if (!dateString) return "";
  const date = moment.utc(dateString);
  const weekdays = [
    "SUNDAY",
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
  ];
  const months = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];

  const weekday = weekdays[date.day()];
  const month = months[date.month()];
  const day = date.date();
  const year = date.year();

  return `${weekday}, ${month} ${day}, ${year}`;
};

// Format expiration date from "2025-11-21" to "11/21"
// Uses UTC to match the payload dates exactly
const formatExpirationDate = (dateString) => {
  if (!dateString) return "";
  const date = moment.utc(dateString);
  const month = date.month() + 1; // moment months are 0-11
  const day = date.date();
  return `${month}/${day}`;
};

// Format date from ISO string or date string to "MM/DD" format
// Uses UTC to match the payload dates exactly
const formatDateShort = (dateString) => {
  if (!dateString) return null;
  try {
    // Parse as UTC and extract date components
    const date = moment.utc(dateString);
    const month = date.month() + 1; // moment months are 0-11
    const day = date.date();
    return `${month}/${day}`;
  } catch (e) {
    return null;
  }
};

// Format contract string from API data
const formatContract = (ticker, strikePrice, optionType, expirationDate) => {
  const formattedExpiration = formatExpirationDate(expirationDate);
  return `${ticker} $${strikePrice} ${optionType} ${formattedExpiration}`;
};

// Format price to currency string
const formatPrice = (price) => {
  if (typeof price === "number") {
    return `$${price.toFixed(2)}`;
  }
  return price;
};

// Calculate P&L percentage: ((exitPrice - aiBuyPrice) / aiBuyPrice) * 100
const calculatePLPercentage = (aiBuyPrice, exitPrice) => {
  if (!aiBuyPrice || aiBuyPrice === 0 || !exitPrice) return 0;
  const percentage = ((exitPrice - aiBuyPrice) / aiBuyPrice) * 100;
  return parseFloat(percentage.toFixed(2));
};

// Helper selector to get cache from state
const getCache = (state) => state.pastAlerts.cache;

// Sagas
function* fetchPastAlerts() {
  try {
    // Get selected period and cache from state
    const selectedPeriod = yield select(getSelectedPeriod);
    const timespan = mapPeriodToTimespan(selectedPeriod);
    const cache = yield select(getCache);

    // Check if data is already cached
    if (cache[timespan]) {
      // Data exists in cache, just dispatch cache hit to update UI
      yield put(pastAlertsCacheHit(timespan));
      return;
    }

    // Data not in cache, fetch from API
    const response = yield axios.get(`${apiURL}/ai-alerts/past/${timespan}`, {
      withCredentials: true,
    });

    yield put(pastAlertsLoadSuccess(response.data, timespan));
  } catch (error) {
    yield put(pastAlertsLoadFailure(error));
  }
}

function* listenPastAlertsLoad() {
  yield takeEvery(PAST_ALERTS_LOAD, fetchPastAlerts);
}

// Root Saga
export function* saga() {
  yield all([fork(listenPastAlertsLoad)]);
}

// Initial State
const INIT_STATE = {
  cache: {}, // Cache keyed by timespan: { "1m": { alertGroups: [], hourlyWinRate: null, overallPL: null }, ... }
  selectedPeriod: "6M",
  loading: false,
  error: null,
};

// Reducer
const reducer = (state = INIT_STATE, action) => {
  switch (action.type) {
    case PAST_ALERTS_LOAD:
      // Loading state is managed by saga cache check
      // If cache hit, saga dispatches PAST_ALERTS_CACHE_HIT instead
      return {
        ...state,
        loading: true,
        error: null,
      };
    case PAST_ALERTS_LOAD_SUCCESS:
      // Store data in cache by timespan
      return {
        ...state,
        cache: {
          ...state.cache,
          [action.timespan]: {
            alertGroups: action.data.alertGroups || [],
            hourlyWinRate: action.data.hourlyWinRate || null,
            overallPL: action.data.overallPL || null,
          },
        },
        loading: false,
        error: null,
      };
    case PAST_ALERTS_CACHE_HIT:
      // Cache hit - data already exists, no API call needed
      return {
        ...state,
        loading: false,
        error: null,
      };
    case PAST_ALERTS_LOAD_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error,
      };
    case PAST_ALERTS_SET_PERIOD:
      return {
        ...state,
        selectedPeriod: action.period,
      };
    default:
      return state;
  }
};

// Selectors
export const getPastAlertsData = (state) => {
  const selectedPeriod = state.pastAlerts.selectedPeriod;
  const timespan = mapPeriodToTimespan(selectedPeriod);
  const cachedData = state.pastAlerts.cache[timespan];
  return cachedData?.alertGroups || [];
};

export const getSelectedPeriod = (state) => state.pastAlerts.selectedPeriod;
export const getPastAlertsLoading = (state) => state.pastAlerts.loading;

// Get cached data for current period
const getCachedDataForPeriod = (state) => {
  const selectedPeriod = state.pastAlerts.selectedPeriod;
  const timespan = mapPeriodToTimespan(selectedPeriod);
  return state.pastAlerts.cache[timespan] || null;
};

// Transform alertGroups into flat array of alerts with formatted fields
export const getFilteredAlerts = (state) => {
  const cachedData = getCachedDataForPeriod(state);
  const alertGroups = cachedData?.alertGroups || [];
  const flattenedAlerts = [];

  alertGroups.forEach((group) => {
    group.alerts.forEach((alert) => {
      // Transform API alert structure to component format
      // Calculate P&L: use profitLoss if available, otherwise calculate from prices
      let plValue = 0;
      if (alert.profitLoss !== undefined && alert.profitLoss !== null) {
        // profitLoss is already a percentage value from API
        plValue = parseFloat(alert.profitLoss.toFixed(2));
      } else if (
        alert.aiExitPrice !== null &&
        alert.aiExitPrice !== undefined &&
        alert.aiBuyPrice
      ) {
        // Calculate from prices if profitLoss not available
        plValue = calculatePLPercentage(alert.aiBuyPrice, alert.aiExitPrice);
      }

      // Use aiBuyDate to determine which date group this alert belongs to
      // aiBuyDate represents when the alert was sent out (correct behavior)
      // Fallback to group.date only if aiBuyDate is missing
      const alertDate = alert.aiBuyDate || group.date;
      const dateGroup = formatDateGroup(alertDate);
      const date = moment.utc(alertDate).toDate();

      const transformedAlert = {
        id: alert.id,
        contract: formatContract(
          alert.ticker,
          alert.strikePrice,
          alert.optionType,
          alert.expirationDate,
        ),
        expiresIn: `Expires in ${alert.daysToExpiration}d`,
        sentiment: alert.sentiment,
        strategy: alert.strategy,
        aiBuyPrice: formatPrice(alert.aiBuyPrice),
        aiBuyDate: formatDateShort(alert.aiBuyDate),
        aiExitPrice:
          alert.aiExitPrice !== null && alert.aiExitPrice !== undefined
            ? formatPrice(alert.aiExitPrice)
            : null,
        aiExitDate: formatDateShort(alert.aiExitDate),
        pl: plValue,
        contractSymbol: alert.contractSymbol,
        dateGroup: dateGroup,
        date: date,
        // Keep original API fields for reference
        _original: alert,
      };

      flattenedAlerts.push(transformedAlert);
    });
  });

  return flattenedAlerts;
};

// Calculate indicator data from cached API response
export const getIndicatorData = (state) => {
  const cachedData = getCachedDataForPeriod(state);
  if (!cachedData) {
    return {
      sinceDate: "",
      tradeCount: 0,
      winRate: "66.67%",
      overallPnL: "+105.25%",
    };
  }

  const alertGroups = cachedData.alertGroups || [];
  const hourlyWinRate = cachedData.hourlyWinRate;
  const overallPL = cachedData.overallPL;

  // Calculate total trade count
  const tradeCount = alertGroups.reduce(
    (sum, group) => sum + (group.alerts?.length || 0),
    0,
  );

  // Find the earliest date based on aiBuyDate (alert sent date)
  let sinceDate = "";
  if (alertGroups.length > 0) {
    // Collect all aiBuyDate values from all alerts
    const allBuyDates = [];
    alertGroups.forEach((group) => {
      (group.alerts || []).forEach((alert) => {
        const buyDate = alert.aiBuyDate || group.date;
        if (buyDate) {
          allBuyDates.push(buyDate);
        }
      });
    });

    if (allBuyDates.length > 0) {
      const earliestDate = moment.utc(
        moment.min(allBuyDates.map((d) => moment.utc(d))),
      );
      const options = { day: "numeric", month: "short", year: "numeric" };
      sinceDate = earliestDate
        .toDate()
        .toLocaleDateString("en-US", options)
        .toUpperCase()
        .replace(",", ",");
    }
  }

  // Format win rate and overall P&L
  const winRateFormatted =
    hourlyWinRate !== null ? `${hourlyWinRate}%` : "66.67%";
  const overallPLFormatted =
    overallPL !== null
      ? `${overallPL >= 0 ? "+" : ""}${overallPL.toFixed(2)}%`
      : "+105.25%";

  return {
    sinceDate: sinceDate,
    tradeCount: tradeCount,
    winRate: winRateFormatted,
    overallPnL: overallPLFormatted,
  };
};

export default reducer;
